/**
 * PathLooseFkGlyph — clickable inline loose-FK-state glyph for a JSON-path row.
 *
 * Rendered next to a property name inside the JSON structure details dialog,
 * mirroring `PathEnumGlyph`. Three states:
 *
 *   • `declared`  — a `LooseRelationship` has `sourcePath === path`. Blue
 *                   chain-link icon; clicking opens the loose-FK editor
 *                   pre-filled with that declaration.
 *   • `pickmode`  — the dialog is in loose-FK pick-mode and the path has no
 *                   declaration yet. Muted chain-link icon, click is handled
 *                   by the row name so the glyph stays purely visual.
 *   • `none`      — render nothing (no declaration, not picking).
 */
import React from 'react';
import { Link2 } from 'lucide-react';

import {
  GLYPH_DECLARED_CLASS,
  GLYPH_MUTED_CLASS,
} from '../grid-glyphs/gridGlyphStyles';

export type PathLooseFkGlyphState = 'declared' | 'pickmode';

type PathLooseFkGlyphProps = {
  state: PathLooseFkGlyphState;
  path: string;
  /** Only fired in `declared` state; `pickmode` is visual-only since the row
   *  name itself is the click target inside pick-mode. */
  onClick?: () => void;
};

const TITLE: Record<PathLooseFkGlyphState, string> = {
  declared: 'Loose FK declared — click to edit',
  pickmode: 'Click this property to declare a loose FK',
};

const CLASS_FOR: Record<PathLooseFkGlyphState, string> = {
  declared: GLYPH_DECLARED_CLASS,
  pickmode: GLYPH_MUTED_CLASS,
};

export const PathLooseFkGlyph = React.memo(function PathLooseFkGlyph({
  state,
  path,
  onClick,
}: PathLooseFkGlyphProps) {
  if (state === 'pickmode') {
    return (
      <span
        className={`${CLASS_FOR[state]} ml-1 px-0.5 shrink-0 inline-flex items-center cursor-default opacity-70`}
        title={TITLE[state]}
        aria-hidden="true"
        data-testid={`path-loose-fk-glyph-${path}`}
        data-state={state}
      >
        <Link2 className="h-3 w-3" aria-hidden="true" />
      </span>
    );
  }
  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        onClick?.();
      }}
      className={`${CLASS_FOR[state]} ml-1 px-0.5 shrink-0 inline-flex items-center`}
      title={TITLE[state]}
      aria-label={TITLE[state]}
      data-testid={`path-loose-fk-glyph-${path}`}
      data-state={state}
    >
      <Link2 className="h-3 w-3" aria-hidden="true" />
    </button>
  );
});
